import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
  Tooltip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import { useTranslation } from "react-i18next";

import { AdminCreditsAPI } from "../../api/adminCreditsApi";
import { extractErrorMessage } from "../../utils/extractErrorMessage";

function normalizeCode(v) {
  return (v ?? "").trim().toUpperCase();
}

function ConfirmDialog({ open, title, description, onClose, onConfirm, loading }) {
  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ opacity: 0.85 }}>
          {description}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Annuler
        </Button>
        <Button onClick={onConfirm} disabled={loading} color="error" variant="contained">
          Supprimer
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function AdminTagsPage() {
  const { t } = useTranslation();

  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(10);

  const [error, setError] = useState("");

  // snack
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [snackSeverity, setSnackSeverity] = useState("success");
  const showSnack = (severity, message) => {
    setSnackSeverity(severity);
    setSnackMsg(message);
    setSnackOpen(true);
  };

  // dialogs
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [dupOpen, setDupOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [current, setCurrent] = useState(null); // tag for edit/delete/duplicate
  const [formCode, setFormCode] = useState("");
  const [formName, setFormName] = useState("");

  const load = async () => {
    setError("");
    setLoading(true);
    try {
      const res = await AdminCreditsAPI.listTags();
      const payload = res?.data ?? res;
      const list = payload?.items ?? payload?.tags ?? payload?.data ?? payload ?? [];
      setRows(Array.isArray(list) ? list : []);
    } catch (e) {
      const msg = extractErrorMessage(e);
      setError(msg);
      showSnack("error", msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filteredRows = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter(
      (r) =>
        String(r.code ?? "").toLowerCase().includes(s) ||
        String(r.name ?? "").toLowerCase().includes(s)
    );
  }, [rows, q]);

  const openCreate = () => {
    setFormCode("");
    setFormName("");
    setCreateOpen(true);
  };

  const openEdit = (tag) => {
    setCurrent(tag);
    setFormCode(tag.code ?? "");
    setFormName(tag.name ?? "");
    setEditOpen(true);
  };

  const openDuplicate = (tag) => {
    setCurrent(tag);
    const base = normalizeCode(tag.code);
    setFormCode(base ? `${base}_COPY` : "");
    setFormName(tag.name ? `${tag.name} (Copy)` : "");
    setDupOpen(true);
  };

  const openDelete = (tag) => {
    setCurrent(tag);
    setConfirmOpen(true);
  };

  const createTag = async () => {
    const code = normalizeCode(formCode);
    const name = (formName ?? "").trim() || code;
    if (!code) return;

    setError("");
    setLoading(true);
    try {
      await AdminCreditsAPI.createTag({ code, name });
      setCreateOpen(false);
      await load();
      showSnack("success", t("admin.tags.snack.created", { code }));
    } catch (e) {
      const msg = extractErrorMessage(e);
      setError(msg);
      showSnack("error", msg);
    } finally {
      setLoading(false);
    }
  };

  const updateTag = async () => {
    if (!current?.id) return;
    const code = normalizeCode(formCode);
    const name = (formName ?? "").trim() || code;
    if (!code) return;

    setError("");
    setLoading(true);
    try {
      await AdminCreditsAPI.updateTag(current.id, { code, name });
      setEditOpen(false);
      await load();
      showSnack("success", t("admin.tags.snack.updated", { code }));
    } catch (e) {
      const msg = extractErrorMessage(e);
      setError(msg);
      showSnack("error", msg);
    } finally {
      setLoading(false);
    }
  };

  const duplicateTag = async () => {
    const code = normalizeCode(formCode);
    const name = (formName ?? "").trim() || code;
    if (!code) return;

    setError("");
    setLoading(true);
    try {
      await AdminCreditsAPI.createTag({ code, name });
      setDupOpen(false);
      await load();
      showSnack("success", t("admin.tags.snack.duplicated", { code }));
    } catch (e) {
      const msg = extractErrorMessage(e);
      setError(msg);
      showSnack("error", msg);
    } finally {
      setLoading(false);
    }
  };

  const deleteTag = async () => {
    if (!current?.id) return;

    setError("");
    setLoading(true);
    try {
      await AdminCreditsAPI.deleteTag(current.id);
      setConfirmOpen(false);
      await load();
      showSnack("success", t("admin.tags.snack.deleted", { code: current.code }));
    } catch (e) {
      const msg = extractErrorMessage(e);
      setError(msg);
      showSnack("error", msg);
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        field: "code",
        headerName: t("admin.tags.columns.code"),
        flex: 0.4,
        minWidth: 160,
        renderCell: (params) => <Chip label={params.value} size="small" />,
      },
      {
        field: "name",
        headerName: t("admin.tags.columns.name"),
        flex: 1,
        minWidth: 220,
      },
      {
        field: "actions",
        headerName: t("admin.tags.columns.actions"),
        width: 170,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          const tag = params.row;
          return (
            <Stack direction="row" spacing={0.5}>
              <Tooltip title={t("common.edit")}>
                <span>
                  <IconButton size="small" onClick={() => openEdit(tag)} disabled={loading}>
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title={t("common.duplicate")}>
                <span>
                  <IconButton size="small" onClick={() => openDuplicate(tag)} disabled={loading}>
                    <ContentCopyOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title={t("common.delete")}>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => openDelete(tag)}
                    disabled={loading}
                    color="error"
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          );
        },
      },
    ],
    [t, loading]
  );

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={900}>
            {t("admin.tags.title")}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            {t("admin.tags.subtitle")}
          </Typography>
        </Box>

        <Stack direction="row" gap={1}>
          <TextField
            size="small"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            label={t("common.search")}
            placeholder={t("admin.tags.searchPlaceholder")}
          />
          <Tooltip title={t("common.refresh")}>
            <span>
              <IconButton onClick={load} disabled={loading}>
                <RefreshOutlinedIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Button variant="contained" startIcon={<AddOutlinedIcon />} onClick={openCreate} disabled={loading}>
            {t("common.add")}
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
        <DataGrid
          autoHeight
          rows={filteredRows}
          columns={columns}
          loading={loading}
          getRowId={(r) => r.id}
          pageSizeOptions={[5, 10, 20, 50]}
          paginationModel={{ pageSize, page: 0 }}
          onPaginationModelChange={(m) => setPageSize(m.pageSize)}
          disableRowSelectionOnClick
          sx={{
            border: 0,
            "& .MuiDataGrid-columnHeaders": { bgcolor: "rgba(0,0,0,0.02)" },
          }}
        />
      </Paper>

      {/* CREATE */}
      <Dialog open={createOpen} onClose={loading ? undefined : () => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("admin.tags.dialog.createTitle")}</DialogTitle>
        <DialogContent dividers>
          <Stack gap={2} sx={{ mt: 1 }}>
            <TextField
              label={t("admin.tags.fields.code")}
              value={formCode}
              onChange={(e) => setFormCode(e.target.value)}
              placeholder="CCNA"
              fullWidth
            />
            <TextField
              label={t("admin.tags.fields.name")}
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              placeholder="CCNA Labs"
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={loading}>{t("common.cancel")}</Button>
          <Button onClick={createTag} disabled={loading || !normalizeCode(formCode)} variant="contained">
            {t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* EDIT */}
      <Dialog open={editOpen} onClose={loading ? undefined : () => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("admin.tags.dialog.editTitle")}</DialogTitle>
        <DialogContent dividers>
          <Stack gap={2} sx={{ mt: 1 }}>
            <TextField
              label={t("admin.tags.fields.code")}
              value={formCode}
              onChange={(e) => setFormCode(e.target.value)}
              fullWidth
            />
            <TextField
              label={t("admin.tags.fields.name")}
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={loading}>{t("common.cancel")}</Button>
          <Button onClick={updateTag} disabled={loading || !normalizeCode(formCode)} variant="contained">
            {t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* DUP */}
      <Dialog open={dupOpen} onClose={loading ? undefined : () => setDupOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t("admin.tags.dialog.duplicateTitle")}</DialogTitle>
        <DialogContent dividers>
          <Stack gap={2} sx={{ mt: 1 }}>
            <TextField
              label={t("admin.tags.fields.code")}
              value={formCode}
              onChange={(e) => setFormCode(e.target.value)}
              fullWidth
            />
            <TextField
              label={t("admin.tags.fields.name")}
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDupOpen(false)} disabled={loading}>{t("common.cancel")}</Button>
          <Button onClick={duplicateTag} disabled={loading || !normalizeCode(formCode)} variant="contained">
            {t("common.duplicate")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* DELETE confirm */}
      <ConfirmDialog
        open={confirmOpen}
        title={t("admin.tags.dialog.deleteTitle")}
        description={t("admin.tags.dialog.deleteText", { code: current?.code ?? "" })}
        loading={loading}
        onClose={() => setConfirmOpen(false)}
        onConfirm={deleteTag}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackOpen}
        autoHideDuration={3500}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackOpen(false)}
          severity={snackSeverity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackMsg}
        </Alert>
      </Snackbar>
    </Container>
  );
}
